import { useState } from 'react';
import { 
  FaSearch, 
  FaUser,
  FaEnvelope,
  FaPhone,
  FaChalkboardTeacher,
  FaUserFriends,
  FaCalendarAlt,
  FaRupeeSign,
  FaCheckCircle,
  FaClock,
  FaTimesCircle
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import { formatPaymentStatus, getPaymentStatusColor } from '../../utils/paymentStatusFormatter';
import { formatDateLocale } from '../../utils/dateFormatter';
import { formatName } from '../../utils/dataTransformers';

function AllStudents() {
  const [trainerFilter, setTrainerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (trainerFilter) params.append('trainerId', trainerFilter);
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/students?${params.toString()}`;
  };

  const { data, error } = useSWR(buildQuery(), Fetcher);
  const students = data?.data?.students || [];
  const pagination = data?.data?.pagination || { total: 0, pages: 1 };

  const filteredStudents = students.filter(student => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const programName = student.program && typeof student.program === 'object' && student.program.programId && typeof student.program.programId === 'object'
        ? student.program.programId.name
        : student.program?.name;
      return (
        student.personalDetails?.name?.toLowerCase().includes(query) ||
        student.personalDetails?.email?.toLowerCase().includes(query) ||
        student.personalDetails?.phone?.includes(searchQuery) ||
        (student.trainer && typeof student.trainer === 'object' && student.trainer.name?.toLowerCase().includes(query)) ||
        (programName && programName.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Student Enrollments</h1>
          <p className="text-gray-600">View and manage all student enrollments in programs</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search enrollments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => {
                setTrainerFilter('');
                setSearchQuery('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Students Grid */}
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600">Failed to load student enrollments</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaUserFriends className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Enrollments Found</h2>
            <p className="text-gray-600">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {filteredStudents.map((student) => (
                <div key={student._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">
                        {formatName(student.personalDetails?.name || student.user?.name || 'N/A')}
                      </h3>
                      {student.program && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <FaChalkboardTeacher className="mr-2 text-gray-400" />
                          <span>
                            {typeof student.program === 'object' && student.program.programId && typeof student.program.programId === 'object'
                              ? student.program.programId.name || student.program.name
                              : student.program.name || 'N/A'}
                          </span>
                        </div>
                      )}
                      {student.trainer && typeof student.trainer === 'object' && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <FaUser className="mr-2 text-gray-400" />
                          <span>{formatName(student.trainer.name)}</span>
                          {student.trainer.service && (
                            <span className="ml-2 text-gray-500">({student.trainer.service})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaEnvelope className="mr-2 text-gray-400" />
                      <span className="truncate">{student.personalDetails?.email || student.user?.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaPhone className="mr-2 text-gray-400" />
                      <span>{(() => {
                        const phone = student.personalDetails?.phone || student.user?.mobile || '';
                        return phone && phone.startsWith('temp_') ? 'Not specified' : (phone || 'N/A');
                      })()}</span>
                    </div>
                    {student.enrollment?.date && (
                      <div className="flex items-center text-sm text-gray-600">
                        <FaCalendarAlt className="mr-2 text-gray-400" />
                        <span>Enrolled: {formatDateLocale(student.enrollment.date)}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Enrollment Status:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(student.enrollment?.status)}`}>
                        {student.enrollment?.status || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Payment Status:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(student.payment?.status)}`}>
                        {formatPaymentStatus(student.payment?.status)}
                      </span>
                    </div>
                    {student.payment && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-bold text-green-600 flex items-center">
                          <FaRupeeSign className="mr-1" />
                          {student.payment.paidAmount || 0} / {student.payment.totalAmount || 0}
                        </span>
                      </div>
                    )}
                    {student.attendance && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Attendance:</span>
                        <span className="font-medium text-gray-900">
                          {student.attendance.attendedSessions || 0} / {student.attendance.totalSessions || 0}
                          {student.attendance.attendancePercentage !== undefined && (
                            <span className="ml-2 text-gray-500">
                              ({Math.round(student.attendance.attendancePercentage)}%)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AllStudents;

